import axios from "axios";
import React, { useEffect, useLayoutEffect, useState } from "react";
import LineIcon from "../images/LINE_Icon.png";
import Footer from "../shindan/Footer";
import Header from "../shindan/header";
import ResultRadarChart from "./RadarChart";
import "./styles.scss";
import SummaryComment from "./SummaryComment";

const useDidMount = (func: Function) =>
    useEffect(() => {
        func();
    }, []);

const useWindowSize = (): number[] => {
    const [size, setSize] = useState([0, 0]);
    useLayoutEffect(() => {
        const updateSize = (): void => {
            setSize([window.innerWidth, window.innerHeight]);
        };

        window.addEventListener("resize", updateSize);
        updateSize();

        return () => window.removeEventListener("resize", updateSize);
    }, []);
    return size;
};

const GET_RESULT_URL = "/apps/know-yourself/result";
//@ts-ignore
const RESULT_UUID = window.answerUuid;

const App: React.VFC = (props) => {
    const args = {
        headers: {
            "Content-Type": "application/json",
        },
        params: {
            //@ts-ignore
            customerId: window.shopifyParameters.customerId,
        },
    };

    const [result, setResult] = useState(null);
    const [displayResults, setDisplayResults] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [windowWidth, _] = useWindowSize();

    useDidMount(() => {
        axios
            .get(`${GET_RESULT_URL}/${RESULT_UUID}.json`, args)
            .then((res) => {
                setResult(res.data);

                handleDisplayResults(res.data);

                appendTextColorStyle(res.data);
            })
            .catch((e) => console.error(e));
    });

    const handleDisplayResults = (data) => {
        // 1. 体質がすべて0点の場合、スコアと解説を要素ごと表示しない -> 1
        // 2. 30点以上の体質を点数が高い順にすべて表示する -> 2
        // 3. 30点以上の体質がない場合、点数が高い順に3つ表示する -> 3
        // 3-1. 同点などで上位3つが4つ以上になる場合、最低点の体質は、下記の優先順位で絞ることで3つにする
        //      気滞＞血瘀＞痰湿＞湿熱＞陽虚＞気虚＞血虚＞陰虚
        // 3-2. 上位3つに入っても、0点の体質は表示しない

        if (
            // 1. 体質がすべて0点の場合、スコアと解説を要素ごと表示しない
            Object.values(data.radar_chart).reduce(
                // @ts-ignore
                (sum, element): number => sum + element,
                0
            ) === 0
        ) {
            setDisplayResults([]);
        } else if (
            // 2. 30点以上の体質を点数が高い順にすべて表示する
            Math.max.apply(Math, Object.values(data.radar_chart)) >= 30
        ) {
            setDisplayResults(
                Object.keys(data.radar_chart)
                    .filter((a) => data.radar_chart[a] >= 30)
                    .map((key) => {
                        return [key, data.radar_chart[key]];
                    })
                    .sort((a, b) => {
                        if (a[1] > b[1]) {
                            // score順並び替え
                            return -1;
                        } else if (a[1] == b[1]) {
                            // scoreが同値の時はkeyの優先順位順で並べる
                            return keyNameToScore(a[0]) > keyNameToScore(b[0])
                                ? -1
                                : 1;
                        } else {
                            return 1;
                        }
                    })
                    .sort((a, b) => (a[1] > b[1] ? -1 : 1))
            );
        } else {
            // 3. 30点以上の体質がない場合、点数が高い順に3つ表示する
            // 3-1. 同点などで上位3つが4つ以上になる場合、最低点の体質は、下記の優先順位で絞ることで3つにする
            //      気滞＞血瘀＞痰湿＞湿熱＞陽虚＞気虚＞血虚＞陰虚
            // 3-2. 上位3つに入っても、0点の体質は表示しない
            const sorted = Object.keys(data.radar_chart)
                .filter((a) => data.radar_chart[a] > 0) // 0点の体質は表示しない
                .map((key) => {
                    return [key, data.radar_chart[key]];
                })
                .sort((a, b) => {
                    if (a[1] > b[1]) {
                        // score順並び替え
                        return -1;
                    } else if (a[1] == b[1]) {
                        // scoreが同値の時はkeyの優先順位順で並べる
                        return keyNameToScore(a[0]) > keyNameToScore(b[0])
                            ? -1
                            : 1;
                    } else {
                        return 1;
                    }
                })
                .slice(0, 3); // 点数が高い順に3つ表示する

            setDisplayResults(sorted);
        }
    };

    const keyNameToScore = (key) => {
        switch (key) {
            case "気滞":
                return 7;
            case "血瘀":
                return 6;
            case "痰湿":
                return 5;
            case "湿熱":
                return 4;
            case "陽虚":
                return 3;
            case "気虚":
                return 2;
            case "血虚":
                return 1;
            case "陰虚":
                return 0;
        }
    };

    const translateEn = (kanjiText) => {
        const mapping = {
            気虚: "KIKYO",
            気滞: "KITAI",
            血虚: "KEKKYO",
            血瘀: "KETSUO",
            痰湿: "TANSHITSU",
            陰虚: "INKYO",
            陽虚: "YOKYO",
            湿熱: "SHITSUNETSU",
        };
        return mapping[kanjiText];
    };

    const text = (kanjiText) => {
        const mapping = {
            気虚: [
                "疲れやすい体力不足タイプ",
                "気・血・水のバランスのうち、身体のエネルギーである「気」が不足している気虚（ききょ）タイプ。 疲れやすく体力不足、胃腸が弱い、食後に眠くなる、汗をかきやすい、風邪を引きやすいなどの傾向があります。基礎代謝や平熱が低い方もいます。",
                "https://sakurawada.jp/pages/comments-kikyo",
                "illust_01_Kikyo@2x.png",
                "https://sakurawada.jp/products/6761683845288",
            ],
            気滞: [
                "ストレスの多い張り詰めタイプ",
                "気・血・水のバランスのうち、身体のエネルギーである「気」が滞り、流れが悪くなっている気滞（きたい）タイプ。ストレスに起因して起こりやすく、胸がつかえる、げっぷやため息が出る、肩こり、頭痛、自律神経のバランスが崩れてイライラや憂鬱になりやすいなどの傾向があります。",
                "https://sakurawada.jp/pages/comments-kitai",
                "illust_02_Kitai@2x.png",
                "https://sakurawada.jp/products/6761694396584",
            ],
            血虚: [
                "栄養が足りないふらふらタイプ",
                "気・血・水のバランスのうち、全身の栄養源となる「血」が不足していたり、「血」を作り出す力が低下している血虚（けっきょ）タイプ。血色が良くない、肌のツヤが足りない、ふらふらと立ちくらみしやすい、不眠になりやすいなどの傾向があります。",
                "https://sakurawada.jp/pages/comments-kekkyo",
                "illust_03_Kekkyo@2x.png",
                "https://sakurawada.jp/products/6761695805608",
            ],
            血瘀: [
                "顔がくすむ血行不良タイプ",
                "気・血・水のバランスのうち「血」の流れが悪い血瘀（けつお）タイプ。顔がくすむ、目の下にクマができる、シミやあざが残りやすい、手足が冷える、頭痛・肩こり・関節痛などの痛みが出やすいなどの傾向があります。",
                "https://sakurawada.jp/pages/comments-ketsuo",
                "illust_04_Ketsuo@2x.png",
                "https://sakurawada.jp/products/6761696886952",
            ],
            痰湿: [
                "体が重だるいむくみタイプ",
                "気・血・水のバランスのうち不要な「水」を溜め込んでいて、水分代謝が悪い痰湿（たんしつ）タイプ。体がむくむ、ニキビができる、頭や体が重だるい、軟便・下痢気味、太りやすいなどの傾向があります。",
                "https://sakurawada.jp/pages/comments-tanshitsu",
                "illust_05_Tanshitsu@2x.png",
                "https://sakurawada.jp/products/6761697345704",
            ],
            陰虚: [
                "うるおい不足のほてりタイプ",
                "身体を慈潤する「陰（血・水・精）」が不足し、体に必要なうるおいが足りない陰虚（いんきょ）タイプ。ほてり・のぼせを感じる、肌が乾燥する、喉が渇く、寝汗をかく、便秘がち、怒りっぽくなるなどの傾向があります。",
                "https://sakurawada.jp/pages/comments-inkyo",
                "illust_06_Inkyo@2x.png",
                "https://sakurawada.jp/products/6761698197672",
            ],
            陽虚: [
                "体が冷える機能低下タイプ",
                "身体を温める力である「陽」が不足している陽虚（ようきょ）タイプ。 体が冷えることで内臓機能が低下し、寒さに弱い、手足が冷たい、トイレが近い、下痢しやすい、むくみやすい、風邪を引きやすいなどの傾向があります。",
                "https://sakurawada.jp/pages/comments-yokyo",
                "illust_07_Yokyo@2x.png",
                "https://sakurawada.jp/products/6761699147944",
            ],
            湿熱: [
                "熱がこもる脂テカテカタイプ",
                "身体に不要な「水」と「熱」が溜まっている湿熱（しつねつ）タイプ。食事量の過多で起こりやすく、気血のめぐりが妨げられることで代謝が悪くなり、体がだるい、暑がり、汗をかきやすい、ニキビができる、口臭が強いなどの傾向があります。",
                "https://sakurawada.jp/pages/comments-shitsunetsu",
                "illust_08_Shitsunetsu@2x.png",
                "https://sakurawada.jp/products/6789454233768",
            ],
        };
        return mapping[kanjiText];
    };

    const appendTextColorStyle = (data) => {
        // displayResultsをstateにいれたものがここで取得できないため、再度ここで計算
        const targetKeys = Object.keys(data.radar_chart)
            .map((key) => {
                return [key, data.radar_chart[key]];
            })
            .sort((a, b) => (a[1] > b[1] ? -1 : 1))
            .slice(0, 3)
            .map((a) => a[0]);

        const allKeys = Object.keys(data.radar_chart);
        const targetIndex = allKeys
            .map((key, index) => (targetKeys.includes(key) ? index + 1 : null))
            .filter((a) => a != null);

        const style = document.createElement("style");
        document.head.appendChild(style);
        const sheet = style.sheet;
        sheet.insertRule(
            `g.recharts-layer.recharts-polar-angle-axis-tick text {fill: #A4AABB;}`
        );
        targetIndex.forEach((index) => {
            sheet.insertRule(
                `g.recharts-layer.recharts-polar-angle-axis-tick:nth-child(${index}) text {fill: #000;}`
            );
        });
    };

    const KekkaNoMikata = () => {
        return (
            <div className="px-10 relative z-10">
                <div
                    className={`accordion bg-gray-light text-black-darkest rounded-lg mb-4 md:mb-6 max-w-sm mx-auto ${
                        isOpen ? "collapsed" : ""
                    }`}
                    id="accordionExample"
                >
                    <div className="accordion-item">
                        <h2 className="accordion-header mb-0" id="headingOne">
                            <button
                                className={`font-bold  text-black-darkest accordion-button relative flex items-center w-full py-4 px-5 text-base text-left !bg-gray-light border-0 transition focus:outline-none rounded-lg ${
                                    isOpen ? "" : "collapsed"
                                }`}
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target="#collapseOne"
                                aria-expanded="false"
                                aria-controls="collapseOne"
                                onClick={() => {
                                    setIsOpen(!isOpen);
                                }}
                            >
                                💡結果の見かた
                            </button>
                        </h2>
                        {isOpen && (
                            <div
                                id="collapseOne"
                                className="accordion-collapse collapse show"
                                aria-labelledby="headingOne"
                                data-bs-parent="#accordionExample"
                            >
                                <div className="accordion-body pb-4 px-5">
                                    <ul className="mt-0 mb-0 mr-0 ml-0 py-4 px-5">
                                        <li>
                                            グラフの面積が小さいほど理想的な健康状態です。
                                        </li>
                                        <li>
                                            30点以上は注意が必要です。50点以上は積極的に改善に取り組んでいきましょう。
                                        </li>
                                        <li>
                                            30点以上が複数でたらバランスよく同時にケアするのがベスト！
                                        </li>
                                        <li>
                                            絞りたい場合は基本的に点数が高いものから優先的に選びましょう。
                                        </li>
                                        <li>
                                            悩んだら↓からLINEでご相談ください :)
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const backPreviousQuestion = () => {};

    return (
        <div className="container mx-auto md:max-w-3xl font-body relative tracking-widest">
            <Header
                currentQuestionNo={0}
                allQuestionCount={0}
                backPreviousQuestion={backPreviousQuestion}
                isHideBackButton={true}
                isHideProgressBar={true}
            />
            <>
                {!!result && (
                    <>
                        <div className="bg-white">
                            <h1 className="text-center font-bold text-lg md:mt-10 mt-8">
                                {result.name}さんの診断結果
                            </h1>
                            {KekkaNoMikata()}
                            <div
                                className="RadarChart mx-auto"
                                style={
                                    windowWidth >= 768
                                        ? { width: "768px", height: "768px" }
                                        : {
                                              width: windowWidth * 0.8,
                                              height:
                                                  (windowWidth * 0.8) / (5 / 3),
                                          }
                                }
                                // style={{ height: "100vw", width: "100vw" }}
                            >
                                {!!displayResults ? (
                                    <ResultRadarChart
                                        data={result.radar_chart}
                                    />
                                ) : (
                                    <>67</>
                                )}
                            </div>
                            <div className="flex flex-col md:flex-row md:-mt-40">
                                <div className="my-5 mx-10 leading-8 md:w-1/2">
                                    {displayResults.map((v) => (
                                        <div className="border-b border-0 border-black border-solid flex justify-between mb-6">
                                            <div>
                                                <span className="text-base font-bold mr-1.5">
                                                    {v[0]}
                                                </span>
                                                <span className="text-xs">
                                                    {translateEn(v[0])}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="text-base font-bold mr-1.5">
                                                    {v[1]}
                                                </span>
                                                <span className="text-xs">
                                                    点
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <SummaryComment data={result.radar_chart} />
                            </div>
                            <div className="text-black-lightest text-center py-5 text-xs mb-20">
                                {result.created_at}
                                {result.count > 0 ? ` ${result.count}回目` : ""}
                                {
                                    //@ts-ignore
                                    window.isLogin ? (
                                        <></>
                                    ) : (
                                        <div className="mt-5 mx-12">
                                            このあと、ご購入時などに会員登録すると、この結果は保存され、アカウントページからいつでも確認できるようになります。
                                        </div>
                                    )
                                }
                            </div>
                        </div>
                        {displayResults.map((v) => (
                            <div className="bg-gray-light text-black-darkest mb-4 md:mb-6">
                                <div className="mx-12 py-10 md:py-24 text-center">
                                    <img
                                        src={`https://skrwd-shindan.herokuapp.com/images/${
                                            text(v[0])[3]
                                        }`}
                                        className="w-32"
                                    />
                                    <div className="text-2xl font-bold mt-3">
                                        {`${v[0]}の体質`}
                                    </div>
                                    <div className="mt-4">
                                        <span className="text-xl font-bold pr-1">
                                            {v[1]}
                                        </span>
                                        <span className="text-xs">点</span>
                                    </div>
                                    <div className="text-lg font-bold py-9">
                                        {text(v[0])[0]}
                                    </div>
                                    <div className="border-b border-0 border-black border-solid md:w-40 mx-auto"></div>
                                    <div className="text-sm tracking-widest leading-10 text-justify mt-6 mb-10 md:px-16">
                                        {text(v[0])[1]}
                                    </div>
                                    <a
                                        href={text(v[0])[2]}
                                        className="skrwd-OKButton md:max-w-xs md:mx-auto"
                                    >
                                        改善アドバイスを見る
                                    </a>
                                    <a
                                        href={text(v[0])[4]}
                                        className="skrwd-OKButton Type-Inverse md:max-w-xs md:mx-auto mt-6"
                                    >
                                        この体質におすすめの商品
                                    </a>
                                </div>
                            </div>
                        ))}

                        <div className="text-center mb-6">
                            改善したいことが見つかりませんか？
                            <br />
                            <a
                                href="https://sakurawada.jp/pages/search-themes"
                                className="underline decoration-blue text-blue"
                            >
                                テーマ別に商品を選ぶ
                            </a>
                            こともできます🔎
                        </div>

                        <div className="bg-gray-light text-black-darkest px-12 mb-4 md:mb-6 py-10 md:py-16">
                            <a
                                href="https://sakurawada.jp/apps/know-yourself?start_question"
                                className="skrwd-OKButton md:max-w-xs md:mx-auto"
                                style={{ marginBottom: "2rem" }}
                            >
                                もう一度診断する
                            </a>
                            <a
                                href="https://sakurawada.jp/pages/constitution-sumally"
                                className="skrwd-OKButton md:max-w-xs md:mx-auto"
                            >
                                体質の考え方を知る
                            </a>
                        </div>

                        <div className="text-tiny md:text-sm text-black-lightest mt-10 mx-12 md:mt-32 text-left md:text-center leading-6">
                            <div className="mb-8">
                                この診断は、自己診断に基づいた簡易的なものです。ご自身の正しい体質を知っていただくためには、お近くの病院や薬局で中医師や漢方薬剤師にご相談ください。
                            </div>
                            <div className="mb-4 md:mb-6 skrwd-BgTextBorder">
                                <span>監修医師</span>
                            </div>
                            <div className="mb-4 md:mb-6">
                                医師・黄田 正忠
                                <br />
                                医療法人社団忠恵会理事長、東洋医学未病対策研究会常任理事
                            </div>
                            <div className="mb-4 md:mb-6">
                                中医師・和田 暁<br />
                                高級中医薬膳伝授師、薬膳アカデミア創立者、世界中医薬学会連合会常務理事
                            </div>
                        </div>

                        <div className="mb-32">
                            <Footer />
                        </div>
                        <div className="ResultBottomFixedBar">
                            <a
                                href="https://lin.ee/TwkoAP4"
                                className="ResultBottomFixedBar-Inner"
                            >
                                <div className="ResultBottomFixedBar-Text">
                                    <div className="ResultBottomFixedBar-Body">
                                        診断結果や商品について、LINEでご相談ください
                                        :)
                                    </div>
                                    <div className="ResultBottomFixedBar-Caption">
                                        ＊ご相談は無料です
                                    </div>
                                </div>
                                <div className="ResultBottomFixedBar-LINE">
                                    <div className="ResultBottomFixedBar-LINE-Button">
                                        <div className="ResultBottomFixedBar-LINE-Button-Image">
                                            <img src={LineIcon} />
                                        </div>
                                        <div className="ResultBottomFixedBar-LINE-Button-Soudan">
                                            相談
                                        </div>
                                    </div>
                                </div>
                            </a>
                        </div>
                    </>
                )}
            </>
        </div>
    );
};

export default App;
